import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import {
  getBlockReport,
  getFilterOptions,
  getMessageMedia,
  getPersonThreads,
  getTemplateStats,
  getThread,
  getThreadMessages,
  getWhatsAppNumbers,
  listThreads,
  markThreadRead,
  whatsappKeys,
  type InsightsParams,
  type MessagePage,
  type PersonThreads,
  type PersonType,
  type ThreadDetail,
  type ThreadListParams,
  type ThreadPage,
  type ThreadSummary,
  type WhatsAppMessage,
  type WhatsAppUnread,
} from "@/api/whatsapp";
import { describeError } from "@/lib/errors";

/** 403 and 404 are answers, not failures — retrying cannot change them. */
export const noRetryOnAnswer = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
    return false;
  }
  return failureCount < 2;
};

/*
 * None of these poll. One loop for the whole app (`useWhatsAppLive`) asks
 * the server what changed and writes the answer into these caches, so an open
 * inbox, a docked panel and the top-bar badge cost one request per tick
 * between them — the user's explicit ask (2026-10-23). They refetch only on
 * mount, focus and invalidation, as any query does.
 */

export function useWhatsAppNumbers() {
  return useQuery({
    queryKey: whatsappKeys.numbers(),
    queryFn: ({ signal }) => getWhatsAppNumbers(signal),
    staleTime: 10 * 60_000,
    retry: noRetryOnAnswer,
  });
}

export type ThreadListData = InfiniteData<ThreadPage>;

export function useThreadList(params: ThreadListParams) {
  return useInfiniteQuery({
    queryKey: whatsappKeys.threadList(params),
    queryFn: ({ pageParam, signal }) => listThreads(params, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // The live loop keeps it current; a refetch on every focus would only
    // repeat what it already did.
    staleTime: 60_000,
    retry: noRetryOnAnswer,
  });
}

export function useFilterOptions(params: InsightsParams, enabled = true) {
  return useQuery({
    queryKey: whatsappKeys.filterOptions(params),
    queryFn: ({ signal }) => getFilterOptions(params, signal),
    enabled,
    staleTime: 5 * 60_000,
    retry: noRetryOnAnswer,
  });
}

export function useBlockReport(params: InsightsParams) {
  return useQuery({
    queryKey: whatsappKeys.blockReport(params),
    queryFn: ({ signal }) => getBlockReport(params, signal),
    staleTime: 60_000,
    retry: noRetryOnAnswer,
  });
}

export function useTemplateStats(params: InsightsParams) {
  return useQuery({
    queryKey: whatsappKeys.templateStats(params),
    queryFn: ({ signal }) => getTemplateStats(params, signal),
    staleTime: 60_000,
    retry: noRetryOnAnswer,
  });
}

export function useThread(id: number | null) {
  return useQuery({
    queryKey: whatsappKeys.thread(id ?? 0),
    queryFn: ({ signal }) => getThread(id as number, signal),
    enabled: id !== null && id > 0,
    staleTime: 60_000,
    retry: noRetryOnAnswer,
  });
}

/** Newest page first; `fetchNextPage` loads the page before the oldest shown. */
export type MessagesData = InfiniteData<MessagePage>;

export function useThreadMessages(threadId: number | null) {
  return useInfiniteQuery({
    queryKey: whatsappKeys.messages(threadId ?? 0),
    queryFn: ({ pageParam, signal }) =>
      getThreadMessages(threadId as number, pageParam, signal),
    initialPageParam: null as number | null,
    getNextPageParam: (last) =>
      last.hasOlder && last.items.length > 0 ? (last.items[0]?.id ?? null) : null,
    enabled: threadId !== null && threadId > 0,
    staleTime: 60_000,
    retry: noRetryOnAnswer,
  });
}

/** Oldest first, every page — what the timeline draws. */
export function flattenMessages(data: MessagesData | undefined): WhatsAppMessage[] {
  if (!data) return [];
  return [...data.pages].reverse().flatMap((page) => page.items);
}

export function usePersonThreads(type: PersonType, id: number) {
  return useQuery({
    queryKey: whatsappKeys.person(type, id),
    queryFn: ({ signal }) => getPersonThreads(type, id, signal),
    enabled: id > 0,
    staleTime: 60_000,
    retry: noRetryOnAnswer,
  });
}

/** Written by the live loop; never fetched on its own. */
export function useWhatsAppUnread(): WhatsAppUnread | undefined {
  const query = useQuery<WhatsAppUnread>({
    queryKey: whatsappKeys.unread(),
    queryFn: () => Promise.reject(new Error("Filled by the live loop")),
    enabled: false,
    staleTime: Infinity,
  });
  return query.data;
}

/**
 * Opening a thread marks it read for everyone — unread is per thread, not per
 * admin, like the providers' own inboxes. Applied to the caches at once; a
 * failure puts the count back and says so (the only toast here: success is
 * silent, it is a side effect of looking).
 */
export function useMarkThreadRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (thread: { id: number; unreadCount: number }) =>
      markThreadRead(thread.id),
    onMutate: (thread) => {
      patchThread(queryClient, thread.id, { unreadCount: 0 });
      queryClient.setQueryData<WhatsAppUnread>(whatsappKeys.unread(), (unread) =>
        unread
          ? {
              messages: Math.max(0, unread.messages - thread.unreadCount),
              threads: Math.max(0, unread.threads - 1),
            }
          : unread,
      );
    },
    onError: (error) => {
      toast.error("Couldn’t mark the chat as read", {
        description: describeError(error),
      });
      void queryClient.invalidateQueries({ queryKey: whatsappKeys.all });
    },
  });
}

/**
 * A message's attachment as something an `<img>` or a link can use. Direct
 * links are used as they are; a proxied one is fetched with the session's
 * token and turned into an object URL, revoked when no longer shown.
 */
export function useMessageMedia(message: WhatsAppMessage, enabled = true) {
  const proxied = message.media?.proxied === true;
  const blob = useQuery({
    queryKey: whatsappKeys.media(message.id),
    queryFn: ({ signal }) => getMessageMedia(message.id, signal),
    enabled: proxied && enabled,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: noRetryOnAnswer,
  });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob.data) return;
    const url = URL.createObjectURL(blob.data);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl(null);
    };
  }, [blob.data]);

  return {
    url: proxied ? objectUrl : (message.media?.url ?? null),
    isLoading: proxied && blob.isPending && enabled,
    error: blob.error,
    retry: () => void blob.refetch(),
  };
}

/** Updates one thread wherever it is cached: lists, its detail, a person's tabs. */
export function patchThread(
  queryClient: QueryClient,
  id: number,
  patch: Partial<ThreadSummary>,
): void {
  queryClient.setQueriesData<ThreadListData>(
    { queryKey: whatsappKeys.threadLists() },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        })),
      },
  );
  queryClient.setQueryData<ThreadDetail>(
    whatsappKeys.thread(id),
    (thread) => thread && { ...thread, ...patch },
  );
  queryClient.setQueriesData<PersonThreads>(
    { queryKey: whatsappKeys.people() },
    (data) =>
      data && {
        ...data,
        threads: data.threads.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      },
  );
}
